import { Model, Factory, belongsTo } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import {
  rand,
  randPastDate,
  randFutureDate,
  randNumber,
  randChanceBoolean,
  randProductName,
  randPhrase,
} from '@ngneat/falso'
import type { MockServer, MirageServer } from '../server.js'
import { getQueryParamValue } from '../utils.js'
import { getTodayDateTimeISO } from '@/utils/date/getter.js'

// Refill status codes matching backend
const RefillStatusCode = {
  EXPIRED: 1,
  CANCELLED: 2,
  USED: 3,
  ACTIVE: 4,
} as const

type RefillStatusCodeType =
  (typeof RefillStatusCode)[keyof typeof RefillStatusCode]

// Status metadata for each status code
const statusMetadata: Record<
  RefillStatusCodeType,
  { label: string; icon: string }
> = {
  [RefillStatusCode.EXPIRED]: {
    label: 'expired',
    icon: 'interface-alert-circle',
  },
  [RefillStatusCode.CANCELLED]: {
    label: 'cancelled',
    icon: 'interface-close-circle',
  },
  [RefillStatusCode.USED]: {
    label: 'used',
    icon: 'interface-check-circle',
  },
  [RefillStatusCode.ACTIVE]: {
    label: 'active',
    icon: 'interface-check',
  },
}

type RefillItemTypeCode = 'MEDICINE' | 'SUPPLY' | 'FOOD'

type ConsultationRefillOverviewAttributes = {
  // PrescriptionRepeat direct fields
  repeats: number
  uses: number
  renew_times: number
  cancelled: boolean
  refill_expiry_date: string | null
  name: string
  status: {
    code: RefillStatusCodeType
    label: string
    icon: string
  }
  status_code: RefillStatusCodeType
}

// Extended type that includes optional RefillItem fields that may be passed during creation
type ConsultationRefillOverviewWithOptionalRefillItemFields =
  ConsultationRefillOverviewAttributes & {
    quantity_display?: string
    instructions?: string | null
    is_expired?: boolean
    item_archived?: boolean
    item_id?: number | null
    item_type?: RefillItemTypeCode | null
  }

export const model = (
  Model as ModelDefinition<ConsultationRefillOverviewAttributes>
).extend({
  consultation: belongsTo('consultation'),
  patient: belongsTo('patient'),
  prescriber: belongsTo('user'),
  refillItem: belongsTo('refillItem'),
})

export const factory = Factory.extend<ConsultationRefillOverviewAttributes>({
  repeats() {
    return randNumber({ min: 1, max: 5 })
  },
  uses() {
    return randNumber({ min: 0, max: 3 })
  },
  renew_times() {
    return randNumber({ min: 0, max: 2 })
  },
  cancelled() {
    return randChanceBoolean({ chanceTrue: 0.1 })
  },
  refill_expiry_date() {
    return randChanceBoolean({ chanceTrue: 0.8 })
      ? randFutureDate({ years: 1 }).toISOString()
      : null
  },
  name() {
    return randProductName()
  },
  status_code() {
    return rand([
      RefillStatusCode.ACTIVE,
      RefillStatusCode.EXPIRED,
      RefillStatusCode.USED,
      RefillStatusCode.CANCELLED,
    ])
  },
  status() {
    // This will be set in afterCreate based on status_code
    return {
      code: RefillStatusCode.ACTIVE,
      ...statusMetadata[RefillStatusCode.ACTIVE],
    }
  },

  afterCreate(item, server) {
    // Set consistent status based on status_code
    const statusCode = item.status_code as RefillStatusCodeType
    const metadata = statusMetadata[statusCode]
    item.update({
      status: {
        code: statusCode,
        ...metadata,
      },
    })

    // Set is_expired and cancelled based on status_code for consistency
    item.update({
      cancelled: statusCode === RefillStatusCode.CANCELLED,
    })

    // Assign a prescriber if not set
    if (!item.prescriber) {
      const users = server.schema.all('user').models
      if (users.length > 0) {
        item.update({ prescriber: rand(users) })
      }
    }

    // Assign a patient if not set and consultation has patients
    if (!item.patient && item.consultation?.patients?.models?.length > 0) {
      item.update({ patient: item.consultation.patients.models[0] })
    }

    // Create a RefillItem if not already set
    if (!item.refillItem) {
      // Extract any RefillItem fields that may have been passed to create()
      // Cast to extended type to access optional RefillItem fields
      const itemWithOptionalFields =
        item as unknown as ConsultationRefillOverviewWithOptionalRefillItemFields
      const quantity_display = itemWithOptionalFields.quantity_display
      const instructions = itemWithOptionalFields.instructions
      const is_expired = itemWithOptionalFields.is_expired
      const item_archived = itemWithOptionalFields.item_archived
      const item_id = itemWithOptionalFields.item_id
      const item_type = itemWithOptionalFields.item_type

      // Parse quantity_display if provided (e.g., "30 tablets" -> quantity: 30, display_unit: "tablets")
      let quantity = randNumber({ min: 1, max: 100 })
      let display_unit = rand(['mg', 'ml', 'tablets', 'capsules', 'g', 'units'])

      if (quantity_display && typeof quantity_display === 'string') {
        const match = quantity_display.match(/^(\d+)\s+(.+)$/)
        if (match) {
          quantity = parseInt(match[1], 10)
          display_unit = match[2]
        }
      }

      const refillItem = server.create('refillItem', {
        name: item.name,
        quantity,
        units: randNumber({ min: 1, max: 10 }),
        usage_size: randNumber({ min: 1, max: 5 }),
        instructions:
          instructions ??
          (randChanceBoolean({ chanceTrue: 0.7 }) ? randPhrase() : null),
        display_amount: String(quantity),
        display_unit,
        created: randPastDate({ years: 1 }).toISOString(),
        modified: getTodayDateTimeISO(),
        is_expired: is_expired ?? statusCode === RefillStatusCode.EXPIRED,
        item_archived: item_archived ?? randChanceBoolean({ chanceTrue: 0.05 }),
        item_id: item_id ?? randNumber({ min: 1, max: 1000 }),
        item_type:
          item_type ??
          (rand(['MEDICINE', 'SUPPLY', 'FOOD']) as RefillItemTypeCode),
      })
      item.update({ refillItem: refillItem })
    }
  },
})

export function routes(server: MockServer) {
  // New unified endpoint (namespace already includes /:provet_id/api)
  server.get(
    '/prescription_repeats',
    function (this: MirageServer, schema, request) {
      // Get all refill overview items
      let items = schema.all('consultationRefillOverview')

      // Handle client filtering from URL params (filter[client]=123)
      const clientFilter = request.queryParams['filter[client]']
      if (clientFilter) {
        // Filter by consultation's client - for simplicity, assume all items belong to same client
        // In a real app, you'd filter by consultation.client_id
      }

      // Handle status filtering from URL params (filter[item_status]=ACTIVE,EXPIRED)
      // The frontend sends filter[item_status] as comma-separated values
      const statusFilterParam = getQueryParamValue(
        request.queryParams['filter[item_status]'],
      )

      if (statusFilterParam) {
        const statusArray = statusFilterParam.split(',')

        // Map status strings to status codes
        const statusCodeMap: Record<string, number> = {
          EXPIRED: RefillStatusCode.EXPIRED,
          CANCELLED: RefillStatusCode.CANCELLED,
          USED: RefillStatusCode.USED,
          ACTIVE: RefillStatusCode.ACTIVE,
        }

        const allowedStatusCodes = statusArray
          .map((s) => statusCodeMap[s.trim()])
          .filter((code): code is number => typeof code === 'number')

        if (allowedStatusCodes.length > 0) {
          items = items.filter((item) =>
            allowedStatusCodes.includes(item.status_code),
          )
        }
      }

      // Handle patient filtering from URL params (filter[patients]=1)
      const patientFilter = request.queryParams['filter[patients]']
      if (patientFilter) {
        const patientId = Array.isArray(patientFilter)
          ? patientFilter[0]
          : patientFilter
        items = items.filter((item) => item.patient?.id === patientId)
      }

      // Build included resources based on include parameter
      const includeParam = request.queryParams.include || []
      const includeTypes = Array.isArray(includeParam)
        ? includeParam
        : includeParam.split(',')

      const included: unknown[] = []
      const includedIds = new Set<string>()

      items.models.forEach((item) => {
        if (includeTypes.includes('patient') && item.patient) {
          const key = `Patient-${item.patient.id}`
          if (!includedIds.has(key)) {
            includedIds.add(key)
            included.push({
              type: 'Patient',
              id: item.patient.id,
              attributes: {
                name: item.patient.name,
              },
            })
          }
        }

        if (includeTypes.includes('prescriber') && item.prescriber) {
          const key = `User-${item.prescriber.id}`
          if (!includedIds.has(key)) {
            includedIds.add(key)
            included.push({
              type: 'User',
              id: item.prescriber.id,
              attributes: {
                first_name: item.prescriber.firstName,
                last_name: item.prescriber.lastName,
              },
            })
          }
        }

        if (includeTypes.includes('refill_item') && item.refillItem) {
          const key = `RefillItem-${item.refillItem.id}`
          if (!includedIds.has(key)) {
            includedIds.add(key)
            included.push({
              type: 'RefillItem',
              id: item.refillItem.id,
              attributes: {
                name: item.refillItem.name,
                quantity: item.refillItem.quantity,
                units: item.refillItem.units,
                usage_size: item.refillItem.usage_size,
                instructions: item.refillItem.instructions,
                display_amount: item.refillItem.display_amount,
                display_unit: item.refillItem.display_unit,
                created: item.refillItem.created,
                modified: item.refillItem.modified,
                is_expired: item.refillItem.is_expired,
                item_archived: item.refillItem.item_archived,
                item_id: item.refillItem.item_id,
                item_type: item.refillItem.item_type,
              },
            })
          }
        }
      })

      // Build response data
      const data = items.models.map((item) => ({
        type: 'PrescriptionRepeat',
        id: item.id,
        attributes: {
          repeats: item.repeats,
          uses: item.uses,
          renew_times: item.renew_times,
          cancelled: item.cancelled,
          refill_expiry_date: item.refill_expiry_date,
          name: item.name,
          status: item.status,
        },
        relationships: {
          patient: item.patient
            ? {
                data: { type: 'Patient', id: item.patient.id },
              }
            : { data: null },
          prescriber: item.prescriber
            ? {
                data: { type: 'User', id: item.prescriber.id },
              }
            : { data: null },
          refill_item: item.refillItem
            ? {
                data: { type: 'RefillItem', id: item.refillItem.id },
              }
            : { data: null },
        },
      }))

      return {
        data,
        included,
      }
    },
  )
}

export function seeds(server: MockServer) {
  const consultationIds = ['1', '2']

  consultationIds.forEach((id) => {
    const consultation = server.schema.find('consultation', id)

    if (consultation) {
      // Create various refill items with different statuses
      const statuses = [
        RefillStatusCode.ACTIVE,
        RefillStatusCode.ACTIVE,
        RefillStatusCode.ACTIVE,
        RefillStatusCode.EXPIRED,
        RefillStatusCode.USED,
        RefillStatusCode.CANCELLED,
      ]

      statuses.forEach((statusCode, index) => {
        server.create('consultationRefillOverview', {
          consultation,
          patient: consultation.patients?.models?.[0] || null,
          status_code: statusCode,
          name: `Refill Item ${index + 1}`,
        })
      })
    }
  })
}
